/**
 * Bill of Sale Preview Modal
 * 
 * Renders a filled OMVIC Bill of Sale PDF in an embedded preview,
 * with download functionality.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, Loader2, FileText } from "lucide-react";

interface BillOfSalePreviewProps {
  open: boolean;
  onClose: () => void;
  pdfBytes: Uint8Array | null;
  dealNumber?: string;
  isGenerating?: boolean;
}

export default function BillOfSalePreview({ open, onClose, pdfBytes, dealNumber, isGenerating }: BillOfSalePreviewProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (pdfBytes) {
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setBlobUrl(null);
    }
  }, [pdfBytes]);

  const handleDownload = () => {
    if (!pdfBytes) return;
    try {
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `BillOfSale_${dealNumber || 'Deal'}.pdf`;
      
      document.body.appendChild(a);
      
      // Dispatch a real MouseEvent to ensure browser security models recognize it as user-triggered
      // and apply the custom download filename instead of falling back to the GUID
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      a.dispatchEvent(clickEvent);
      
      // Delay removal and revocation to let the browser initiate the download using the blob reference
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b bg-card shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-heading font-bold">
                  Bill of Sale Preview
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  {dealNumber ? `Deal ${dealNumber}` : 'OMVIC Bill of Sale'} — Review and edit fields before downloading
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 pr-8">
              <Button
                onClick={handleDownload}
                disabled={!pdfBytes || isGenerating}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* PDF Preview Area */}
        <div className="flex-1 overflow-hidden bg-muted/30">
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-muted-foreground font-medium">Generating Bill of Sale...</p>
            </div>
          ) : blobUrl ? (
            <iframe
              src={blobUrl + '#toolbar=0&navpanes=0'}
              className="w-full h-full border-0"
              title="Bill of Sale Preview"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
              <FileText className="h-12 w-12 opacity-30" />
              <p>No PDF generated yet</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
